import { useMemo } from "react";
import { useSelector } from "react-redux";

import { type RootState } from "src/common/rootState.type";
import {
  type PackageSize,
  getStorageFee,
} from "@cielo-blu-team/amazon-fee-lib-v2";

const buildPackageSize = (
  length: number | undefined,
  width: number | undefined,
  height: number | undefined,
  weight: number | undefined
): PackageSize | undefined => {
  const l = Number(length);
  const w = Number(width);
  const h = Number(height);
  const wg = Number(weight);
  if (
    !Number.isFinite(l) ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    !Number.isFinite(wg) ||
    l <= 0 ||
    w <= 0 ||
    h <= 0 ||
    wg <= 0
  ) {
    return undefined;
  }
  return { length: l, width: w, height: h, weight: wg };
};

/**
 * 在庫保管手数料をクライアント側で算出するカスタムフック。
 *
 * - Redux の `feePatterns.storageFee.tiers` と product の梱包寸法・カテゴリから `getStorageFee` を呼ぶ
 * - nodeId には product.feeCategory（websiteDisplayGroup）を渡す。v2 の isApparelNodeId が対応
 */
export const useStorageFee = (): {
  normalStorageFee: number | undefined;
  specialStorageFee: number | undefined;
} => {
  const { product } = useSelector((state: RootState) => state.top);
  const storageFeeContent = useSelector(
    (state: RootState) => state.feePatterns.storageFee
  );

  return useMemo(() => {
    if (!storageFeeContent || !product) {
      return {
        normalStorageFee: undefined,
        specialStorageFee: undefined,
      };
    }

    const packageSize = buildPackageSize(
      product.packageLength,
      product.packageWidth,
      product.packageHeight,
      product.packageWeight
    );
    if (!packageSize) {
      return {
        normalStorageFee: undefined,
        specialStorageFee: undefined,
      };
    }

    const result = getStorageFee(
      product.feeCategory ?? "",
      packageSize,
      storageFeeContent.tiers
    );
    if (result === null) {
      return {
        normalStorageFee: undefined,
        specialStorageFee: undefined,
      };
    }

    return {
      normalStorageFee: result.normalStorageFee,
      specialStorageFee: result.specialStorageFee,
    };
  }, [product, storageFeeContent]);
};
